import {
  Component,
  Input,
  Output,
  EventEmitter,
  ElementRef,
  ViewChild,
  OnInit,
  ChangeDetectorRef,
  SimpleChanges,
  OnChanges,
} from '@angular/core';
import { Animations } from 'app/animations';
import { GenericObject } from 'ngx-web-api';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject, combineLatest } from 'rxjs';
import { debounceTime, filter, distinctUntilChanged, take } from 'rxjs/operators';
import { FocusService } from 'app/focus.service';
import { ScrollEventService } from '../scroll-event.service';
import { ThemeService } from 'app/core/services/theme.service';
import { PagePosition } from 'app/domain/page-position.enum';
import { ViewportService } from 'app/core/services/viewport.service';

@Component({
  selector: 'fts-quantities',
  templateUrl: './quantities.component.html',
  styleUrls: ['./quantities.component.scss'],
  animations: [Animations.expanded],
})
export class QuantitiesComponent implements OnInit, OnChanges {
  @Input()
  selectedQuantity: number;
  @Input()
  needsOverlay = false;
  @Input()
  label = null;
  @Input()
  isInOrderTree = false;
  @Input()
  disabled = false;
  @Input()
  minimumQuantity = 1;
  @Input()
  bubbleUpErrors = false;
  @Input()
  describedBy: string;
  @Output()
  selectedQuantityChange = new EventEmitter<number>(true);
  @Output()
  quantityError: EventEmitter<GenericObject<string[]>> = new EventEmitter();

  quantities = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
  @ViewChild('quantity', { static: true })
  quantity: ElementRef;

  @ViewChild('dropDownArrow', { static: true })
  dropDownArrow: ElementRef;

  showMinWarning = false;
  randId = `qt-list-${Math.random().toString(36).substring(2)}`;

  protected inputQuantity = new Subject<{ quantity: number; warn: boolean }>();
  private inputQuantity$: Observable<{ quantity: number; warn: boolean }> = this.inputQuantity.asObservable();

  private isThemeInNeedOfScrollBlock: boolean = false;

  constructor(
    private cdRef: ChangeDetectorRef,
    private translateService: TranslateService,
    private focusService: FocusService,
    private scrollEventService: ScrollEventService,
    private themeService: ThemeService,
    private viewPortService: ViewportService
  ) {}

  ngOnInit() {
    combineLatest([this.viewPortService.viewport$, this.themeService.theme])
      .pipe(take(1))
      .subscribe(([viewport, theme]) => {
        this.isThemeInNeedOfScrollBlock =
          theme.hasSinglePageMenu &&
          theme.orderTreePosition === PagePosition.Right &&
          !theme.compactHeader &&
          !theme.minifiedHeader &&
          !viewport.isMobile &&
          !('ontouchstart' in document.documentElement);
      });
    this.inputQuantity$
      .pipe(
        filter(q => !!q),
        debounceTime(500),
        distinctUntilChanged((prev, cur) => prev.quantity === cur.quantity)
      )
      .subscribe(({ quantity, warn }) => {
        if (quantity < this.minimumQuantity) {
          this.selectedQuantity = this.minimumQuantity;
          this.selectedQuantityChange.emit(this.minimumQuantity);
          this.checkValueAfterFocusOut(null);
          if (warn) {
            this.showMinValueWarning();
          }
          return;
        }
        if (this.showMinWarning) {
          this.showMinWarning = false;
        }

        this.selectedQuantityChange.emit(quantity);
      });

    this.onQuantityChanged(this.selectedQuantity || 1, false);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['minimumQuantity'] && !!this.minimumQuantity) {
      this.quantities = [this.minimumQuantity, ...this.quantities.map(q => q + this.minimumQuantity)];
    }
  }
  openStatusChanged(isOpen) {
    const maxWaitInMills = 1000;
    let counter = 0;
    const intervalRate = 100;

    const intervalId = setInterval(() => {
      const el = <HTMLElement>document.querySelector('body > .dropdown > .dropdown-menu');
      if (el !== null && isOpen) {
        this.focusService.focusElement = new ElementRef(el);
        this.focusService.applyFocus();
        clearInterval(intervalId);
      } else if (el === null && !isOpen) {
        this.dropDownArrow.nativeElement.focus();
      }

      //Guarding the cases where the el is not found + forcing focus on dropdown arrow on close
      if (counter >= maxWaitInMills) {
        clearInterval(intervalId);
      }
      counter += intervalRate;
    }, intervalRate);
  }

  blockScrolling() {
    let currentScrollPosition = window.scrollY;
    this.scrollEventService.blockingScrollEvent.next(true);
    window.onscroll = () => {
      window.scrollTo({ top: currentScrollPosition });
    };
    window.onwheel = () => {
      this.resetScrollBEhavior();
    };
  }

  resetScrollBEhavior() {
    this.scrollEventService.blockingScrollEvent.next(false);
    window.onscroll = undefined;
  }

  quantityMask(rawValue: string) {
    const valueWithoutMaskChars = rawValue.replace(/\D+/g, '');
    if (valueWithoutMaskChars && valueWithoutMaskChars.length > 1) {
      return [/[1-9]/, /\d/];
    }
    return [/[1-9]/];
  }

  onQuantityChanged(quantity: string | number, warn = true) {
    if (this.isThemeInNeedOfScrollBlock) {
      this.blockScrolling();
    }
    const parsedQuantity = typeof quantity === 'string' ? parseInt(quantity, 10) : quantity;
    if (!quantity || isNaN(parsedQuantity)) {
      return;
    }
    this.inputQuantity.next({ quantity: parsedQuantity, warn: warn });
  }

  checkValueAfterFocusOut(value: any) {
    this.resetScrollBEhavior();
    if (!value) {
      this.quantity.nativeElement.value = this.selectedQuantity;
    }
  }

  private showMinValueWarning() {
    if (this.bubbleUpErrors) {
      const minQError = this.translateService.instant('component.quantity_selector.min_warning_long', {
        minQuantity: this.minimumQuantity,
      });
      this.quantityError.emit({ minQuantity: [minQError] });
      setTimeout(() => this.quantityError.emit({ minQuantity: [] }), 3000);
      return;
    }
    this.showMinWarning = true;
    setTimeout(() => ((this.showMinWarning = false), this.cdRef.detectChanges()), 3000);
  }
}
